import { useCallback } from 'react'

import { CountryCode as CountryIsoCode, parsePhoneNumber } from 'libphonenumber-js'

import {
  Address,
  CalendarBookingPayload,
  MEETING_FORMATS,
  PROVIDER_TYPES,
  ProviderEnums,
  ProviderInfo,
} from '../../../models'
import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { TREATMENT_MODE } from '../../common/constants/appointmentModalityConstants'
import { convertProviderTreatmentModalityToTreatmentOption } from '../../common/constants/providerTreatmentModalityConstants'
import {
  convertTreatmentOptionValueForBackend,
  TREATMENT_OPTIONS,
  TREATMENT_OPTIONS_TYPE,
} from '../../common/constants/treatmentOptions'
import { formatAddressForSubmission } from '../../locationFinder/utils'
import { isValidCTPOCombination } from '../../programConfigs'
import {
  getIndividualTherapyCTPOForProvider,
  isProviderAnyTypeOf,
  isProviderBlendedCareType,
} from '../../providers/utils'
import { BOOKING_CHANNEL_TYPE, CalendarAppointment, CalendarAppointmentWithModality, PHONE_TYPES } from '../types'

interface UseBookingPayloadParam {
  appointment: CalendarAppointment | CalendarAppointmentWithModality
  provider: ProviderInfo
  appointmentType?: string
  isNear: boolean
  address?: Address | null
  treatmentType: TREATMENT_OPTIONS_TYPE | null
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null
  fallbackTreatmentOption: TREATMENT_OPTIONS_TYPE | null
  timeZone: string
  userId: string
  outsideOnboarding: boolean
  appointmentDurationMinutes: number
  countryIsoCode: CountryIsoCode
  meetingFormat: MEETING_FORMATS | null
  phone: string | null
  phoneType: PHONE_TYPES
  smsConsent: boolean
  searchId?: string
  isDirectPath?: boolean
  isClientRebooking?: boolean
  isDirectBookingLink?: boolean
  isCNTBooking?: boolean
  episodeId?: string
  childLyraId?: string
  childEmail?: string
  providerPatientId?: string | null
  requestId: string
  isParentLedLctTeenBooking?: boolean
  programTaxonomy?: ProviderProgramTaxonomy
  directBookingLinkCreateDate?: string
  userSearchLocationId?: string | null
  isFavoritedProvidersBooking?: boolean
  isSavedProviderResultsReengagementEmails?: boolean
  childPhone?: string
  childPhoneCountryCode?: CountryIsoCode
  childSMSConsent?: boolean
  guardianLyraId?: string
  isDAMinorProgramEnabled?: boolean
  title?: string
}

/**
 * Hook that helps create the payload to create a new appointment used crossplatform
 */
export const useBookingPayload = () => {
  const makePayload = useCallback(
    ({
      appointment,
      provider,
      appointmentType,
      address,
      isNear,
      selectedTreatmentOption,
      fallbackTreatmentOption,
      timeZone,
      userId,
      treatmentType,
      appointmentDurationMinutes,
      countryIsoCode,
      childPhoneCountryCode,
      meetingFormat,
      phone,
      phoneType,
      smsConsent,
      searchId,
      isDirectPath,
      isClientRebooking,
      isDirectBookingLink,
      outsideOnboarding,
      isCNTBooking = false,
      episodeId,
      providerPatientId,
      childLyraId,
      childEmail,
      childPhone,
      childSMSConsent,
      requestId,
      isParentLedLctTeenBooking,
      programTaxonomy,
      directBookingLinkCreateDate,
      userSearchLocationId,
      isFavoritedProvidersBooking = false,
      isSavedProviderResultsReengagementEmails = false,
      isDAMinorProgramEnabled = false,
      guardianLyraId: passedGuardianLyraId,
      title,
    }: UseBookingPayloadParam): CalendarBookingPayload => {
      const isCoach = isProviderAnyTypeOf({ provider, providerTypes: [PROVIDER_TYPES[ProviderEnums.COACH]] })
      const isBlendedCareProvider = isProviderBlendedCareType({ provider })
      let locationId
      let addressDisplay = ''

      // Check if the appointment has a specific location selected (e.g., multi-location providers)
      // appointmentLocation only exists on CalendarAppointmentWithModality, not CalendarAppointment
      const selectedLocation =
        'appointmentLocation' in appointment ? appointment.appointmentLocation ?? undefined : undefined

      if (isNear) {
        const visitType = provider.supportedVisitTypes

        // Determine if this is an in-person visit that needs address information
        const isInPersonVisit =
          (visitType === TREATMENT_MODE.BOTH_VIDEO_INPERSON && meetingFormat !== MEETING_FORMATS.VIDEO) ||
          visitType === TREATMENT_MODE.ONLY_INPERSON
        locationId = ''
        // Priority: selected location -> nearest provider address -> empty
        if (isInPersonVisit) {
          if (selectedLocation) {
            addressDisplay = formatAddressForSubmission(selectedLocation)
            // SelectedLocation implies that there was usage of the IPC, therefore we do not need to pass a locationId and can just pass
            // the address as is since we don't need locationId for lookup on LW-API
          } else if (provider.nearestAddress) {
            addressDisplay = formatAddressForSubmission(provider.nearestAddress)
          }
        }
      } else if (address && Object.keys(address).length) {
        // Onsite provider booking flow
        if (selectedLocation?.id) {
          // SelectedLocation implies that there was usage of the IPC, therefore we do not need to pass a locationId and can just pass
          // the address as is since we don't need locationId for lookup on LW-API
          locationId = ''
          addressDisplay = formatAddressForSubmission(selectedLocation)
        } else {
          locationId = address.id?.toString() ?? ''
          addressDisplay = formatAddressForSubmission(address)
        }
      }

      // Only bookings with a searchId present are triage searches
      // Favorited providers bookings are also not triage searches by definition, even if a searchId is present
      const isBookingConnectedToTriageSearch = !!searchId && !isFavoritedProvidersBooking

      if (isBookingConnectedToTriageSearch) {
        if (fallbackTreatmentOption) {
          treatmentType = fallbackTreatmentOption
        } else treatmentType = selectedTreatmentOption
      } else if (selectedTreatmentOption) {
        treatmentType = selectedTreatmentOption
      } else if (isCoach) {
        treatmentType = TREATMENT_OPTIONS.COACHING
      }
      const treatmentTypeForBE = treatmentType ? convertTreatmentOptionValueForBackend(treatmentType) : treatmentType
      const providerModalities = provider.modalities

      const selectedProviderModality = providerModalities?.find(
        (modality) => convertProviderTreatmentModalityToTreatmentOption(modality) === treatmentType,
      )

      const phoneNumberInfo = phone ? parsePhoneNumber(phone, countryIsoCode) : null

      let bookingChannel
      if (isDirectPath) {
        if (isSavedProviderResultsReengagementEmails) {
          bookingChannel = BOOKING_CHANNEL_TYPE.SAVED_PROVIDER_RESULTS_REENGAGEMENT_EMAILS
        } else {
          bookingChannel = BOOKING_CHANNEL_TYPE.DIRECT_PATH
        }
      } else if (isSavedProviderResultsReengagementEmails) {
        bookingChannel = BOOKING_CHANNEL_TYPE.SAVED_PROVIDER_RESULTS_REENGAGEMENT_EMAILS
      } else if (isCNTBooking) {
        bookingChannel = BOOKING_CHANNEL_TYPE.CNT_BOOKING
      } else if (isClientRebooking) {
        bookingChannel = BOOKING_CHANNEL_TYPE.CLIENT_REBOOKING
      } else if (isDirectBookingLink || (outsideOnboarding && !isFavoritedProvidersBooking)) {
        bookingChannel = BOOKING_CHANNEL_TYPE.DIRECT_BOOKING_LINK
      } else if (isBookingConnectedToTriageSearch) {
        bookingChannel = BOOKING_CHANNEL_TYPE.TRIAGE
      } else if (isFavoritedProvidersBooking) {
        bookingChannel = BOOKING_CHANNEL_TYPE.FAVORITED_PROVIDERS
      } else {
        bookingChannel = undefined
      }

      let guardianLyraId = passedGuardianLyraId
      if ((isParentLedLctTeenBooking || (isDAMinorProgramEnabled && provider.isDAProviderMigrated)) && childLyraId) {
        // This is parent-led lct teen initial booking. In this case, we:
        // 1. set guardianLyraId to be the userId as the userId passed is the guardian's Lyra ID for child bookings.
        // 2. set userId to be childLyraId so appointment is booked under the child instead of the guardian.
        // 3. clear the childLyraId field as it's not longer applicable
        // Scenario 2: DA Minor booking. Parent booking for a minor, we want the same payload.
        guardianLyraId = userId
        userId = childLyraId
        childLyraId = undefined
      }

      // Context for this "default" CTPO when no CTPO was given + a standaloneTreatmentOption
      // is given: https://github.com/LyraHealth/ui-monorepo/pull/11801/files#r1953126541
      // For direct path individual therapy, we use the provider's Partner attribute to determine
      // the correct CTPO (LC, ICAS, or DA) instead of always defaulting to BlendedCare.
      // It's safe to select the first program_taxonomy because all taxonomies will have the same Partner.
      const finalProgramTaxonomy =
        isDirectPath &&
        treatmentType === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY &&
        !isValidCTPOCombination(programTaxonomy)
          ? getIndividualTherapyCTPOForProvider({ programTaxonomy: provider.program_taxonomy?.[0] })
          : programTaxonomy

      return {
        address: addressDisplay,
        appointmentDuration: appointmentDurationMinutes ?? 50,
        appointmentType:
          appointmentType || provider.supportedVisitTypes === TREATMENT_MODE.ONLY_VIDEO ? 'video' : 'googleCalendar',
        bookingChannel,
        childLyraId,
        childEmail: !!childEmail ? encodeURIComponent(childEmail) : undefined,
        guardianLyraId,
        details: `${treatmentTypeForBE} https://care.lyrahealth.com/secure/care-manager/patient-detail?uid=${userId}`,
        episodeId,
        locationId: !isCoach ? locationId : undefined,
        lyraProviderId: provider.lyra_id,
        meetingFormat,
        phone: phoneNumberInfo?.number,
        phoneCountryCode: countryIsoCode,
        phoneType,
        providerPatientId,
        requestId,
        searchId,
        selectedProviderModality,
        smsConsent: isBlendedCareProvider || provider.isDAProviderMigrated ? smsConsent : false,
        startDate: appointment.date,
        startTime: appointment.time,
        supportedVisitTypes: provider.supportedVisitTypes,
        timeZone,
        title: title ?? 'New Appointment',
        treatmentType: treatmentTypeForBE,
        userId,
        userSearchLocationId,
        ...(!!finalProgramTaxonomy && { programTaxonomy: finalProgramTaxonomy }),
        directBookingLinkCreateDate,
        ...(!!childPhone && { childPhone, childPhoneCountryCode }),
        ...(childSMSConsent !== undefined && { childSMSConsent }),
      }
    },
    [],
  )
  return { makePayload }
}
